import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { consultationsApi, type ConsultationSlot, type OpenDay } from '@/api/consultationsApi';
import { spokenTime } from '@/utils/formatters';

const DAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
               'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS_AHEAD = 30;

// People choose an appointment by part of day before they choose a clock time,
// so the grid is split the way the decision is actually made.
const PARTS = [
  { key: 'morning', label: 'Morning', until: 12 * 60 },
  { key: 'afternoon', label: 'Afternoon', until: 17 * 60 },
  { key: 'evening', label: 'Evening', until: 24 * 60 },
] as const;

const iso = (d: Date) => d.toISOString().slice(0, 10);

const minutesOf = (time: string) => {
  const [h, m] = time.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
};

function dayParts(date: string) {
  const [y, m, d] = date.split('-').map(Number);
  const when = new Date(Date.UTC(y, (m || 1) - 1, d || 1));
  return { weekday: DAY[when.getUTCDay()], day: d, month: MONTH[(m || 1) - 1] };
}

export interface SlotSelection {
  date: string;
  time: string;
  display: string;
}

/**
 * The schedule, unboxed. Availability is public, so a signed-out visitor sees
 * real times; the login gate comes at the pay button.
 */
export function SlotPicker({
  selected,
  onSelect,
  disabled = false,
}: {
  selected: SlotSelection | null;
  onSelect: (slot: SlotSelection | null) => void;
  disabled?: boolean;
}) {
  const [days, setDays] = useState<OpenDay[] | null>(null);
  const [activeDate, setActiveDate] = useState<string | null>(null);
  const [slotState, setSlotState] = useState<{ date: string | null; slots: ConsultationSlot[] }>({
    date: null,
    slots: [],
  });

  useEffect(() => {
    let cancelled = false;
    const today = new Date();
    const until = new Date(today);
    until.setDate(until.getDate() + DAYS_AHEAD);
    consultationsApi
      .getOpenDays(iso(today), iso(until))
      .then((res) => {
        if (cancelled) return;
        const open = res.data.days || [];
        setDays(open);
        setActiveDate((current) => current ?? open[0]?.date ?? null);
      })
      .catch(() => {
        if (!cancelled) setDays([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!activeDate) return;
    let cancelled = false;
    consultationsApi
      .getSlots(activeDate)
      .then((res) => {
        if (!cancelled) setSlotState({ date: activeDate, slots: res.data.slots || [] });
      })
      .catch(() => {
        if (!cancelled) setSlotState({ date: activeDate, slots: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [activeDate]);

  const loadingSlots = activeDate !== null && slotState.date !== activeDate;
  const slots = slotState.date === activeDate ? slotState.slots : [];
  const index = days?.findIndex((d) => d.date === activeDate) ?? -1;
  const capacityVaries = slots.some((slot) => slot.available > 1);

  const step = (delta: number) => {
    if (!days || index < 0) return;
    const next = days[index + delta];
    if (!next) return;
    setActiveDate(next.date);
    onSelect(null);
  };

  if (days === null) {
    return (
      <div className="space-y-4" data-loading>
        <Skeleton className="h-14 w-full rounded-lg" />
        <Skeleton className="h-40 w-full rounded-lg" />
      </div>
    );
  }

  if (days.length === 0) {
    return (
      <p className="border-t py-10 text-muted-foreground">
        No times are open at the moment. Please check back shortly, or book an in-person session
        at a clinic.
      </p>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-1 border-b pb-3">
        <button
          type="button"
          onClick={() => step(-1)}
          disabled={disabled || index <= 0}
          aria-label="Earlier days"
          className="rounded-md p-1.5 text-muted-foreground transition hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-30"
        >
          <ChevronLeft className="size-4" />
        </button>

        <ul className="no-scrollbar flex flex-1 gap-1 overflow-x-auto">
          {days.map((day) => {
            const { weekday, day: num, month } = dayParts(day.date);
            const active = day.date === activeDate;
            return (
              <li key={day.date}>
                <button
                  type="button"
                  disabled={disabled}
                  aria-pressed={active}
                  onClick={() => {
                    setActiveDate(day.date);
                    onSelect(null);
                  }}
                  className={`flex min-w-[64px] flex-col items-center rounded-lg px-3 py-2 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                    active
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-muted'
                  }`}
                >
                  <span className={`text-xs ${active ? 'text-primary-foreground/75' : 'text-muted-foreground'}`}>
                    {weekday}
                  </span>
                  <span className="text-xl font-semibold leading-tight tabular-nums">{num}</span>
                  <span className={`text-xs ${active ? 'text-primary-foreground/75' : 'text-muted-foreground'}`}>
                    {month}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>

        <button
          type="button"
          onClick={() => step(1)}
          disabled={disabled || index < 0 || index >= days.length - 1}
          aria-label="Later days"
          className="rounded-md p-1.5 text-muted-foreground transition hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-30"
        >
          <ChevronRight className="size-4" />
        </button>
      </div>

      {loadingSlots ? (
        <div className="space-y-4 py-6">
          <Skeleton className="h-10 w-full rounded-lg" />
          <Skeleton className="h-10 w-full rounded-lg" />
        </div>
      ) : slots.length === 0 ? (
        <p className="py-10 text-muted-foreground">
          Nothing left on this day. Try the next one.
        </p>
      ) : (
        <div className="divide-y">
          {PARTS.map((part, partIndex) => {
            const from = partIndex === 0 ? 0 : PARTS[partIndex - 1].until;
            const inPart = slots.filter((s) => {
              const m = minutesOf(s.time);
              return m >= from && m < part.until;
            });
            if (inPart.length === 0) return null;
            return (
              <div key={part.key} className="flex flex-col gap-3 py-5 sm:flex-row sm:gap-6">
                <p className="w-24 shrink-0 pt-2 text-sm text-muted-foreground">{part.label}</p>
                <div className="flex flex-wrap gap-2">
                  {inPart.map((slot) => {
                    const active = selected?.date === activeDate && selected?.time === slot.time;
                    const lastOne = capacityVaries && slot.available === 1;
                    return (
                      <button
                        key={slot.time}
                        type="button"
                        disabled={disabled}
                        aria-pressed={active}
                        aria-label={lastOne ? `${spokenTime(slot.time)}, last one available` : undefined}
                        onClick={() =>
                          onSelect(
                            active || !activeDate
                              ? null
                              : { date: activeDate, time: slot.time, display: spokenTime(slot.time) },
                          )
                        }
                        className={`flex w-24 flex-col items-center rounded-lg px-2 py-2 text-[15px] tabular-nums transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                          active
                            ? 'bg-primary font-semibold text-primary-foreground'
                            : 'bg-muted/60 text-foreground hover:bg-muted'
                        } disabled:opacity-50`}
                      >
                        <span>{spokenTime(slot.time)}</span>
                        {lastOne && (
                          <span
                            className={`text-[11px] ${
                              active ? 'text-primary-foreground/80' : 'text-muted-foreground'
                            }`}
                          >
                            last one
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
